import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import useFieldFilterValues from '../../hooks/useFieldFilterValues';

function displayValue(value) {
  const text = String(value ?? '').trim();
  if (!text || text === '[]' || text === '{}') return ANALYTICS_COPY.valueInclusionBlank;
  return text;
}

export default function ValueInclusionPicker({
  fieldId,
  campaignIds = [],
  leadsFormIds = [],
  selected = [],
  onChange,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const { values, isLoading } = useFieldFilterValues(fieldId, campaignIds, leadsFormIds);
  const selectedValues = useMemo(
    () => selected.filter((item) => values.includes(item) || values.length === 0),
    [selected, values]
  );

  if (!fieldId) return null;

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="subtitle2" fontWeight={700}>
          {ANALYTICS_COPY.valueInclusionTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {ANALYTICS_COPY.valueInclusionOptional}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {ANALYTICS_COPY.valueInclusionHint}
      </Typography>

      {!isLoading && !values.length ? (
        <Typography variant="body2" color="text.secondary">
          {ANALYTICS_COPY.valueInclusionEmpty}
        </Typography>
      ) : (
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={values}
          value={selectedValues}
          inputValue={query}
          loading={isLoading}
          disabled={disabled || !values.length}
          getOptionLabel={displayValue}
          isOptionEqualToValue={(option, value) => option === value}
          onInputChange={(_event, next, reason) => {
            if (reason === 'input' || reason === 'clear') setQuery(next);
          }}
          onChange={(_event, next) => onChange(next)}
          renderTags={() => null}
          noOptionsText={ANALYTICS_COPY.valueInclusionEmpty}
          renderOption={(props, option, { selected: isSelected }) => {
            const { key, ...optionProps } = props;
            return (
              <Box
                component="li"
                key={key}
                {...optionProps}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Checkbox
                  size="small"
                  checked={isSelected}
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0.25 }}
                />
                <Typography variant="body2" fontWeight={600} noWrap>
                  {displayValue(option)}
                </Typography>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={ANALYTICS_COPY.valueInclusionSearch}
            />
          )}
        />
      )}

      {selectedValues.length ? (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
          {selectedValues.map((value) => (
            <Chip
              key={value || 'blank'}
              label={displayValue(value)}
              size="small"
              disabled={disabled}
              onDelete={() => onChange(selected.filter((item) => item !== value))}
              sx={{ fontWeight: 600, borderRadius: 1 }}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}
